import React, { useContext, useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import parse from 'html-react-parser';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { getRequest, postRequest, deleteRequest } from '../../api/ApiController';
import { InfoIcon, ErrorIcon, TaskIcon, PermissionIcon, NeedIcon, EmergencyIcon, BellSlashIcon, DeleteIcon, OutlookMsgIcon, PaperClipIcon, BotIcon } from "../svg/svgIcons";
import { Dialog } from 'primereact/dialog';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { confirmDialog } from 'primereact/confirmdialog';
import '../../assets/css/support.css';
import '../../assets/css/overlayLayers.css';
import { Avatar } from 'primereact/avatar';
import { DateTime } from "luxon";
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tag } from 'primereact/tag';
import MyEditor from '../../components/Editor';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Select } from "antd";
import { addLocale } from 'primereact/api';
import { InfinitySpin } from 'react-loader-spinner';
import { resolveSystemI18nComment } from '../../utils/systemI18nComment';
import { formatSupportTicketStatusLabel } from '../../utils/supportTicketStatus';
import { antdPopupOverlayProps, primeOverlayPanelProps } from '../../utils/overlayLayers';
import {
    buildAwaitingUserOptions,
    createSidebarMutationSequencer,
    mergeSupportTicketDetail,
    resolveAwaitingUserId,
    toStableUserId,
} from '../../utils/supportTicketSidebarState';
import { uiT } from '../../utils/uiTranslate';

addLocale('sk', {
    firstDayOfWeek: 1,
    dayNames: ['Nedeľa','Pondelok','Utorok','Streda','Štvrtok','Piatok','Sobota'],
    dayNamesShort: ['Ne','Po','Ut','St','Št','Pi','So'],
    dayNamesMin: ['Ne','Po','Ut','St','Št','Pi','So'],
    monthNames: ['Január','Február','Marec','Apríl','Máj','Jún','Júl','August','September','Október','November','December'],
    monthNamesShort: ['Jan','Feb','Mar','Apr','Máj','Jún','Júl','Aug','Sep','Okt','Nov','Dec'],
    today: uiT('Common.Today'),
    clear: uiT('Common.Delete'),
    chooseDate: uiT('Common.ChooseDate')
});

function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
}

export default function SupportTicketDetail({ ticket = null, ticketId: ticketIdProp = null }) {
    const ticketIdResolved = ticketIdProp ?? ticket?.id;
    const [state, dispatch] = useContext(DatabaseContext);
    const { user } = useContext(AuthContext);
    const { toast, getUserColor, getUserInitials, setDialogText, setVisible, openFilePreview, getDateFormat } = useContext(UtilContext);
    const { t, locale } = useContext(LocaleContext);
    const dateFmt = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';

    const [formSupportTicket, setFormSupportTicket] = useState(ticket ?? null);
    const [loadingTicket, setLoadingTicket] = useState(() => {
        if (!ticketIdResolved) return false;
        return !(ticket?.id != null && Number(ticket.id) === Number(ticketIdResolved));
    });
    const [ticketLoadError, setTicketLoadError] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [images, setImages] = useState(null);
    const [addedUser, setAddedUser] = useState("");
    const [commentClick, setCommentClick] = useState(false);
    const [updatableComment, setUpdatableComment] = useState(false);
    const [sortDescending, setSortDescending] = useState(true);
    const [closeTab, setCloseTab] = useState(false);
    const op = useRef(null);
    const scrollRef = useRef(null);
    const findedIds = useRef([]);
    const lastConfirmedTicketRef = useRef(ticket ?? null);
    const sidebarMutationSeqRef = useRef(createSidebarMutationSequencer());
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            // Invalidate in-flight sidebar mutations so stale responses cannot write after unmount.
            sidebarMutationSeqRef.current.begin();
        };
    }, []);

    function loopThroughDOM(obj) {
        if (Array.isArray(obj)) {
            for (let i = 0; i < obj.length; i++) {
                if (obj[i]?.props?.children) loopThroughDOM(obj[i].props.children);
                if (obj[i]?.props?.['data-id']) findedIds.current.push(obj[i].props['data-id']);
            }
        } else if (obj?.props?.children != null) {
            loopThroughDOM(obj.props.children);
        } else if (obj?.props?.['data-id']) {
            findedIds.current.push(obj.props['data-id']);
        }
    }

    function scheduleSupportImagesLoad(ticketId) {
        if (!ticketId) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                getRequest(`/api/support_tickets/images/${ticketId}`)
                    .then((r) => setImages(r.data && r.data !== "{}" ? r.data : []))
                    .catch(() => setImages([]));
            });
        });
    }

    useEffect(() => {
        if (!ticketIdResolved) return;
        setImages(null);
        const hasListSeed = ticket?.id != null && Number(ticket.id) === Number(ticketIdResolved);
        if (!hasListSeed) {
            setLoadingTicket(true);
        }
        setTicketLoadError(null);
        getRequest(`/api/support_tickets/${ticketIdResolved}`)
            .then((res) => {
                if (!isMountedRef.current) return;
                const data = res?.data ?? res;
                lastConfirmedTicketRef.current = data;
                setFormSupportTicket(data);
                setLoadingTicket(false);
                scheduleSupportImagesLoad(data.id);
            })
            .catch((err) => {
                if (!isMountedRef.current) return;
                setTicketLoadError(err?.message || t('SupportTicketDetail.LoadFailed'));
                setLoadingTicket(false);
            });
    }, [ticketIdResolved, ticket?.id, t]);

    useEffect(() => {
        if (formSupportTicket?.is_unread) {
            const fd = new FormData();
            fd.append('ticket_id', formSupportTicket.id);
            fd.append('user_id', user.id);
            postRequest(`/api/support_tickets/${formSupportTicket.id}/mark-as-viewed`, fd, true)
                .then(() => {
                    dispatch({ type: 'updateTicketViewed', payload: formSupportTicket.id, value: false });
                })
                .catch(() => {});
        }
    }, [formSupportTicket?.id, formSupportTicket?.is_unread, user?.id, dispatch]);

    const applyTicketResponse = (response, options = {}) => {
        const { refreshImages = false, mutationToken = null } = options;
        if (!isMountedRef.current) return false;
        if (mutationToken != null && !sidebarMutationSeqRef.current.isCurrent(mutationToken)) {
            return false;
        }
        if (response?.status === 200 && response?.data) {
            setFormSupportTicket((prev) => {
                const merged = mergeSupportTicketDetail(prev, response.data);
                lastConfirmedTicketRef.current = merged;
                return merged;
            });
            dispatch({ type: 'support_ticket_updated_in_detail', payload: response.data });
            if (refreshImages) {
                getRequest(`/api/support_tickets/images/${response.data.id}`).then((r) => {
                    if (!isMountedRef.current) return;
                    setImages(r.data && r.data !== "{}" ? r.data : []);
                }).catch(() => {
                    if (!isMountedRef.current) return;
                    setImages([]);
                });
            }
            return true;
        }
        return false;
    };

    const refreshTicket = (mutationToken = null) => {
        const ticketId = formSupportTicket?.id ?? lastConfirmedTicketRef.current?.id;
        if (!ticketId) return;
        getRequest(`/api/support_tickets/${ticketId}`)
            .then((res) => {
                if (!isMountedRef.current) return;
                if (mutationToken != null && !sidebarMutationSeqRef.current.isCurrent(mutationToken)) {
                    return;
                }
                const data = res?.data ?? res;
                setFormSupportTicket((prev) => {
                    const merged = mergeSupportTicketDetail(prev, data);
                    lastConfirmedTicketRef.current = merged;
                    return merged;
                });
                getRequest(`/api/support_tickets/images/${data.id}`).then((r) => {
                    if (!isMountedRef.current) return;
                    setImages(r.data && r.data !== "{}" ? r.data : []);
                }).catch(() => {
                    if (!isMountedRef.current) return;
                    setImages([]);
                });
            })
            .catch(() => {});
    };

    const refreshNavbarCount = () => {
        getRequest("/api/support_tickets/count").then((res) => {
            dispatch({ type: 'load_support_tickets_open_count', count: res?.data?.count ?? res?.count ?? 0 });
        }).catch(() => {});
    };

    const formik = useFormik({
        initialValues: { description: '' },
        enableReinitialize: true,
        onSubmit: (values) => {
            setDialogText(t('SupportTicketDetail.Saving'));
            setVisible(true);
            findedIds.current = [];
            const formData = new FormData();
            const files = Array.isArray(values.files) ? values.files : [];
            formData.append('description', values.description);
            formData.append('contractor_id', user.id);
            formData.append('ticket_id', formSupportTicket.id);
            files.forEach((file) => formData.append('files[]', file));
            const parsed = typeof values.description === 'string' ? parse(values.description || "") : values.description;
            if (parsed && parsed.props?.children) loopThroughDOM(parsed);
            formData.append('mentions', findedIds.current.filter(onlyUnique));
            postRequest('/api/support_comments', formData, true)
                .then((response) => {
                    setFormSupportTicket(response.data);
                    getRequest(`/api/support_tickets/images/${response.data.id}`).then((r) => setImages(r.data && r.data !== "{}" ? r.data : []));
                    refreshNavbarCount();
                    if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
                    formik.resetForm();
                    setCommentClick(false);
                    setVisible(false);
                    toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.CommentSaved') });
                })
                .catch(() => {
                    setVisible(false);
                    if (!navigator.online) alert(t('SupportTicketDetail.OfflineQueued'));
                    else alert(t('SupportTicketDetail.ActionFailed'));
                });
        },
    });

    const formikUpdate = useFormik({
        initialValues: { description: '', id: null },
        enableReinitialize: true,
        onSubmit: (values) => {
            setDialogText(t('SupportTicketDetail.Saving'));
            setVisible(true);
            findedIds.current = [];
            const formData = new FormData();
            const files = Array.isArray(values.files) ? values.files : [];
            formData.append('description', values.description);
            files.forEach((file) => formData.append('files[]', file));
            const parsed = typeof values.description === 'string' ? parse(values.description || "") : values.description;
            if (parsed && parsed.props?.children) loopThroughDOM(parsed);
            formData.append('mentions', findedIds.current.filter(onlyUnique));
            postRequest(`/api/support_comments/update/${values.id}`, formData, true)
                .then((response) => {
                    setFormSupportTicket(response.data);
                    getRequest(`/api/support_tickets/images/${response.data.id}`).then((r) => setImages(r.data && r.data !== "{}" ? r.data : []));
                    if (closeTab) dispatch({ type: 'close_tab', id: state.selectedTab });
                    formikUpdate.resetForm();
                    setUpdatableComment(false);
                    setVisible(false);
                    toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.CommentUpdated') });
                })
                .catch(() => setVisible(false));
        },
    });

    const checkNotifiation = () => formSupportTicket?.mentions?.filter((m) => m.id === user.id)[0]?.pivot?.notification;

    const changeStatus = (value) => {
        const mutationToken = sidebarMutationSeqRef.current.begin();
        const previous = lastConfirmedTicketRef.current;
        setFormSupportTicket((prev) => (prev ? { ...prev, status: value } : prev));
        const formData = new FormData();
        formData.append('status', value);
        postRequest(`/api/support_tickets/updateStatus/${formSupportTicket.id}`, formData, true)
            .then((response) => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) return;
                if (!applyTicketResponse(response, { mutationToken })) refreshTicket(mutationToken);
                refreshNavbarCount();
                toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.StatusUpdated') });
            })
            .catch(() => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) return;
                if (previous) setFormSupportTicket(previous);
                alert(t('SupportTicketDetail.ActionFailed'));
            });
    };

    const changeNotification = () => {
        const mutationToken = sidebarMutationSeqRef.current.begin();
        const previous = lastConfirmedTicketRef.current;
        const formData = new FormData();
        formData.append('value', Number(!checkNotifiation()));
        postRequest(`/api/support_tickets/updateNotification/${formSupportTicket.id}`, formData, true)
            .then((response) => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) return;
                if (!applyTicketResponse(response, { mutationToken })) refreshTicket(mutationToken);
                refreshNavbarCount();
                toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: checkNotifiation() ? t('SupportTicketDetail.NotificationsDisabled') : t('SupportTicketDetail.NotificationsEnabled') });
            })
            .catch(() => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) return;
                if (previous) setFormSupportTicket(previous);
                alert(t('SupportTicketDetail.ActionFailed'));
            });
    };

    const formatDateToInput = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const setAwaitingUser = (value) => {
        const awaitingId = toStableUserId(value);
        const mutationToken = sidebarMutationSeqRef.current.begin();
        const previous = lastConfirmedTicketRef.current;
        setFormSupportTicket((prev) => {
            if (!prev) return prev;
            const options = buildAwaitingUserOptions(prev);
            const selected = options.find((o) => o.id === awaitingId);
            return {
                ...prev,
                awaiting_user_id: awaitingId,
                awaiting_user: selected
                    ? { id: selected.id, username: selected.name, employee: { name: selected.name } }
                    : (awaitingId == null ? null : prev.awaiting_user),
            };
        });
        setDialogText(t('SupportTicketDetail.Saving'));
        setVisible(true);
        const formData = new FormData();
        formData.append('awaiting_user_id', awaitingId ?? '');
        postRequest(`/api/support_tickets/setAwaitingUser/${formSupportTicket?.id}`, formData, true)
            .then((response) => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) {
                    setVisible(false);
                    return;
                }
                setVisible(false);
                if (!applyTicketResponse(response, { mutationToken })) refreshTicket(mutationToken);
                toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.UserSet') });
            })
            .catch(() => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) return;
                setVisible(false);
                if (previous) setFormSupportTicket(previous);
                alert(t('SupportTicketDetail.ActionFailed'));
            });
    };

    const setDeadline = (value) => {
        const mutationToken = sidebarMutationSeqRef.current.begin();
        const previous = lastConfirmedTicketRef.current;
        const nextDeadline = value ? formatDateToInput(value) : null;
        setFormSupportTicket((prev) => (prev ? { ...prev, deadline: nextDeadline } : prev));
        setDialogText(t('SupportTicketDetail.Saving'));
        setVisible(true);
        const formData = new FormData();
        formData.append('deadline', value ? formatDateToInput(value) : "");
        postRequest(`/api/support_tickets/setDeadline/${formSupportTicket?.id}`, formData, true)
            .then((response) => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) {
                    setVisible(false);
                    return;
                }
                setVisible(false);
                if (response?.status !== 200) {
                    if (previous) setFormSupportTicket(previous);
                    alert(response?.message || t('SupportTicketDetail.ActionFailed'));
                    return;
                }
                if (response?.data) applyTicketResponse(response, { mutationToken });
                else refreshTicket(mutationToken);
                refreshNavbarCount();
                toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.DeadlineSet') });
            })
            .catch(() => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) return;
                setVisible(false);
                if (previous) setFormSupportTicket(previous);
                alert(t('SupportTicketDetail.ActionFailed'));
            });
    };

    const addUser = () => {
        const mutationToken = sidebarMutationSeqRef.current.begin();
        setDialogText(t('SupportTicketDetail.Saving'));
        setVisible(true);
        const formData = new FormData();
        formData.append('mention', addedUser);
        postRequest(`/api/support_tickets/addUser/${formSupportTicket.id}`, formData, true)
            .then((response) => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) {
                    setVisible(false);
                    return;
                }
                if (!applyTicketResponse(response, { mutationToken })) refreshTicket(mutationToken);
                refreshNavbarCount();
                setShowAddUser(false);
                setAddedUser("");
                setVisible(false);
                toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.UserAdded') });
            })
            .catch(() => {
                if (!sidebarMutationSeqRef.current.isCurrent(mutationToken)) return;
                setVisible(false);
                alert(t('SupportTicketDetail.ActionFailed'));
            });
    };

    const deleteFiles = (img) => {
        confirmDialog({
            message: t('SupportTicketDetail.DeleteFileConfirmMessage'),
            header: t('SupportTicketDetail.DeleteConfirmHeader'),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Common.No'),
            rejectLabel: t('Common.Yes'),
            accept: () => toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('ServiceTask.RequestCancelled'), life: 3000 }),
            reject: () => getRequest(`/api/support_tickets/deleteImage/${img.id}`)
                .then(() => { toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.FileDeleted') }); refreshTicket(); getRequest(`/api/support_tickets/images/${formSupportTicket.id}`).then((r) => setImages(r.data && r.data !== "{}" ? r.data : [])); })
                .catch(() => toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('SupportTicketDetail.FileDeleteFailed'), life: 3000 }))
        });
    };

    const deleteTicket = (id) => {
        confirmDialog({
            message: t('SupportTicketDetail.DeleteTicketConfirmMessage'),
            header: t('SupportTicketDetail.DeleteConfirmHeader'),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            acceptLabel: t('Common.No'),
            rejectLabel: t('Common.Yes'),
            accept: () => toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('ServiceTask.RequestCancelled'), life: 3000 }),
            reject: () => deleteRequest(`/api/support_tickets/${id}`)
                .then(() => { refreshNavbarCount(); toast?.current?.show({ severity: 'info', summary: t('ServiceTask.Confirmed'), life: 3000, detail: t('SupportTicketDetail.TicketDeleted') }); dispatch({ type: 'close_tab', id: state.selectedTab }); })
                .catch(() => toast?.current?.show({ severity: 'warn', summary: t('ServiceTask.Rejected'), detail: t('SupportTicketDetail.TicketDeleteFailed'), life: 3000 }))
        });
    };

    const content = (rawDescription) => {
        const resolved = resolveSystemI18nComment(rawDescription, t);
        const display = resolved.text ?? '';
        const inner = resolved.type === 'legacy' && String(display).includes('<')
            ? parse(String(display))
            : display;
        return <div className="flex align-items-center"><BotIcon/><div className="ml-2">{inner}</div></div>;
    };

    function renderTicketType(type) {
        switch (type) {
            case 'Info': return <div className='flex gap-2 text-gray-800 items-center'><InfoIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Info')}</span></div>;
            case 'Error': return <div className='flex gap-2 text-gray-800 items-center'><ErrorIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Error')}</span></div>;
            case 'Task': return <div className='flex gap-2 text-gray-800 items-center'><TaskIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Task')}</span></div>;
            case 'Permission': return <div className='flex gap-2 text-gray-800 items-center'><PermissionIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Permission')}</span></div>;
            case 'Need': return <div className='flex gap-2 text-gray-800 items-center'><NeedIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Need')}</span></div>;
            case 'Emergency': return <div className='flex gap-2 text-gray-800 items-center'><EmergencyIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Emergency')}</span></div>;
            default: return <div className='flex gap-2 text-gray-800 items-center'><InfoIcon width={8} height={8}/><span>{t('SupportTicketList.Type.Info')}</span></div>;
        }
    }

    function renderTicketStatus(statusVal) {
        const s = statusVal ?? formSupportTicket?.status;
        const statusLabel = formatSupportTicketStatusLabel(s, t);
        switch (s) {
            case 'Vytvorený': return <Tag className='bg-gray-400'><span className="text-base font-normal">{statusLabel}</span></Tag>;
            case 'V riešení': return <Tag className='bg-yellow-400'><span className="text-base font-normal">{statusLabel}</span></Tag>;
            case 'Čaká sa': return <Tag className='bg-blue-300'><span className="text-base font-normal">{statusLabel}</span></Tag>;
            case 'Čaká sa na odpoveď': return <Tag className='bg-indigo-400'><span className="text-base font-normal">{statusLabel}</span></Tag>;
            case 'Pozastavený': return <Tag className='bg-red-300'><span className="text-base font-normal">{statusLabel}</span></Tag>;
            case 'Vyriešený': return <Tag className='bg-green-400'><span className="text-base font-normal">{statusLabel}</span></Tag>;
            default: return <Tag className='bg-gray-400'><span className="text-base font-normal">{statusLabel}</span></Tag>;
        }
    }

    if (loadingTicket) {
        return (
            <div className="flex items-center justify-center w-full h-64">
                <InfinitySpin width="120" color="#333" />
            </div>
        );
    }
    if (ticketLoadError || !formSupportTicket) {
        return (
            <div className="p-4">
                <Message severity="error" text={ticketLoadError || t('SupportTicketDetail.NotFound')} />
                <button type="button" className="mt-2 px-4 py-2 bg-gray-600 text-white rounded" onClick={() => dispatch({ type: 'close_tab', id: state.selectedTab })}>{t('Common.Close')}</button>
            </div>
        );
    }

    const ticketObj = formSupportTicket;
    const comments = (ticketObj?.comments?.slice() ?? []).sort((a, b) => sortDescending ? b.id - a.id : a.id - b.id);
    const mentionUser = ticketObj?.mentions?.filter((m) => toStableUserId(m.id) === toStableUserId(user.id))[0];
    const awaitingUserId = resolveAwaitingUserId(ticketObj);
    const mentionOptions = buildAwaitingUserOptions(ticketObj);

    return (
        <>
            <div className='relative flex flex-col bg-gray-100 w-full py-4 px-4'>
                <div className='w-auto bg-white rounded-md shadow-md px-10 py-6 h-[calc(100vh_-_170px)]'>
                    <div className='flex font-bold text-sm text-blue-800'>
                        <button className='mr-4' onClick={() => dispatch({ type: 'close_tab', id: state.selectedTab })}>
                            <i title={t('Common.Back')} className="pi pi-arrow-left" style={{ fontSize: '1.25rem', marginRight: 5 }}></i>
                        </button>
                        ISG-SK-Support / {ticketObj.defined_id}
                    </div>
                    <div className='text-2xl font-light text-black py-6'>{ticketObj.theme}</div>
                    <div className='flex flex-row gap-11'>
                        <div className='basis-2/3'>
                            <div className='flex flex-row justify-between items-center w-full p-4 border border-zinc-600 rounded-sm mb-2'>
                                <div>
                                    <Avatar className='p-overlay-badge' title={ticketObj.contractor?.employee?.name || ticketObj.contractor?.username} label={getUserInitials(ticketObj.contractor?.employee?.name || ticketObj.contractor?.username)} shape="circle" style={{ backgroundColor: getUserColor(ticketObj.contractor?.username), color: '#ffffff', marginRight: '5px' }} />
                                    {ticketObj.contractor?.employee?.name || ticketObj.contractor?.username} {t('ServiceTaskDetail.CreatedBy')} {DateTime.fromISO(ticketObj.created_at).toFormat(dateFmt)} {t('SupportTicketDetail.AtTime')} {DateTime.fromISO(ticketObj.created_at).toFormat("HH:mm")}
                                </div>
                                <button onClick={() => setShowDetail(true)} className='font-semibold'>{t('SupportTicketDetail.Detail')}</button>
                            </div>
                            <div className='max-h-[calc(100vh_-_450px)] overflow-y-auto flex-1 min-w-0'>
                            <div>
                                <div className='flex justify-between'>
                                    <h1 className='text-xl font-semibold text-black mb-4'>{t('SupportTicketDetail.Activity')}</h1>
                                    <button onClick={() => setSortDescending((prev) => !prev)} title={sortDescending ? t('SupportTicketDetail.SortFromOldest') : t('SupportTicketDetail.SortFromNewest')} className="text-gray-600 hover:text-gray-800">
                                        <i className={`pi ${sortDescending ? 'pi-sort-amount-down' : 'pi-sort-amount-up'}`}></i>
                                    </button>
                                </div>
                                <div ref={scrollRef} className='h-fit overflow-y-auto'>
                                    {sortDescending && (
                                        <div className='flex flex-row mb-3'>
                                            <div className='basis-1/10'>
                                                <Avatar title={user?.employee?.name || user?.username} label={getUserInitials(user?.employee?.name || user?.username)} shape="circle" style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff', marginRight: '5px' }} />
                                            </div>
                                            {!commentClick ? (
                                                <div className='flex flex-col basis-9/10'>
                                                    <input type='text' onClick={() => setCommentClick(true)} placeholder={t('SupportTicketDetail.AddCommentPlaceholder')} className='app-activity-add-comment-input px-1 py-1 border focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40' />
                                                </div>
                                            ) : (
                                                <form onSubmit={formik.handleSubmit} className="flex flex-col basis-9/10">
                                                    <div className="app-activity-comment-editor-border-wrap">
                                                    <MyEditor editorValue={formik.values.description} autofocus={true} fileAccept="*" formik={formik} activityMessageRadius={true} />
                                                    </div>
                                                    <div className='flex flex-row gap-1'>
                                                        <button type="button" onClick={() => setCommentClick(false)} className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-gray-600 rounded-md hover:bg-gray-400 focus:outline-none disabled:opacity-25'>{t('Common.Cancel')}</button>
                                                        <button type="submit" className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600 disabled:opacity-25'>{t('Common.Save')}</button>
                                                    </div>
                                                </form>
                                            )}
                                        </div>
                                    )}
                                    {comments.map((comment, index, array) => (
                                        <div className='flex flex-row w-full justify-between mb-3' key={comment.id}>
                                            <div className='basis-1/10'>
                                                <Avatar title={comment?.contractor?.employee?.name || comment?.contractor?.username} label={getUserInitials(comment.contractor?.employee?.name || comment.contractor?.username)} shape="circle" style={{ backgroundColor: getUserColor(comment.contractor?.username), color: '#ffffff', marginRight: '5px' }} />
                                            </div>
                                            <div className='flex flex-col grow basis-8/10'>
                                                <div className='mb-1'><span className='font-semibold'>{comment.contractor?.employee?.name || comment.contractor?.username}</span> {DateTime.fromISO(comment.updated_at).toFormat(dateFmt)} {t('SupportTicketDetail.AtTime')} {DateTime.fromISO(comment.updated_at).toFormat("HH:mm")}</div>
                                                {comment?.by_system === 0 ? (
                                                    <div>
                                                        {(array.length - 1 === index && updatableComment) ? (
                                                            <form onSubmit={formikUpdate.handleSubmit}>
                                                                <div className="app-activity-comment-editor-border-wrap">
                                                                <MyEditor editorValue={formikUpdate.values.description} autofocus={true} fileAccept="*" formik={formikUpdate} activityMessageRadius={true} />
                                                                </div>
                                                                <div className='flex flex-row gap-1'>
                                                                    <button type="button" onClick={() => setUpdatableComment(false)} className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-gray-600 rounded-md hover:bg-gray-400 focus:outline-none disabled:opacity-25'>{t('Common.Cancel')}</button>
                                                                    <button type="submit" className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25'>{t('Common.Edit')}</button>
                                                                </div>
                                                            </form>
                                                        ) : <MyEditor editorValue={comment.description} hideToolbar={true} hideFileUpload={true} compact={true} formik={null} activityMessageRadius={true} />}
                                                    </div>
                                                ) : <Message severity="info" content={() => content(comment?.description)} className='justify-start bg-gray-100' />}
                                            </div>
                                            {array.length - 1 === index && comment.contractor_id === user.id && ticketObj?.status !== "Vyriešený" && comment?.by_system === 0 && (
                                                <div className='flex basis-1/10'>
                                                    {!updatableComment && <button onClick={(e) => op.current?.toggle(e)}><i className='pi pi-ellipsis-v'></i></button>}
                                                    <OverlayPanel ref={op}>
                                                        <div className='flex flex-col'>
                                                            <div className='flex flex-row items-center hover:bg-gray-200'>
                                                                <button className='px-4 py-3' onClick={() => { formikUpdate.setFieldValue('id', comment.id); formikUpdate.setFieldValue('description', comment.description); setUpdatableComment(true); op.current?.hide(); }}><i className='pi pi-pencil mr-2'></i><span className='font-semibold'>{t('Common.Edit')}</span></button>
                                                            </div>
                                                        </div>
                                                    </OverlayPanel>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {!sortDescending && ticketObj?.status !== "Vyriešený" && (
                                        <div className='flex flex-row mt-3'>
                                            <div className='basis-1/10'><Avatar title={user?.employee?.name || user?.username} label={getUserInitials(user?.employee?.name || user?.username)} shape="circle" style={{ backgroundColor: getUserColor(user?.username), color: '#ffffff', marginRight: '5px' }} /></div>
                                            {!commentClick ? (
                                                <div className='flex flex-col basis-9/10'><input type='text' onClick={() => setCommentClick(true)} placeholder={t('SupportTicketDetail.AddCommentPlaceholder')} className='app-activity-add-comment-input px-1 py-1 border focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40' /></div>
                                            ) : (
                                                <form onSubmit={formik.handleSubmit} className="flex flex-col basis-9/10">
                                                    <div className="app-activity-comment-editor-border-wrap">
                                                    <MyEditor editorValue={formik.values.description} autofocus={true} fileAccept="*" formik={formik} activityMessageRadius={true} />
                                                    </div>
                                                    <div className='flex flex-row gap-1'>
                                                        <button type="button" onClick={() => setCommentClick(false)} className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-gray-600 rounded-md hover:bg-gray-400 focus:outline-none disabled:opacity-25'>{t('Common.Cancel')}</button>
                                                        <button type="submit" className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25'>{t('Common.Save')}</button>
                                                    </div>
                                                </form>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {images == null && <Skeleton height="4rem" className="mb-2"></Skeleton>}
                            {images?.length > 0 && (
                                <div>
                                    <h2 className='text-xl'>{t('SupportTicketDetail.Attachments')}</h2>
                                    <div className='flex flex-row flex-wrap w-full h-full border border-separate border-gray-400 rounded-md bg-white mb-4'>
                                        {images.map((img) => {
                                            const ext = (img.filename || '').split('.').pop();
                                            if (ext === 'msg') {
                                                return (
                                                    <div className='text-black h-auto w-52' key={img.id}>
                                                        <div className='flex flex-row p-2'>
                                                            <div onClick={() => openFilePreview({ url: img.url, filename: img.filename })} className="cursor-pointer">
                                                                <div className="w-[100px] h-[65px] flex items-center justify-center bg-gray-100 border rounded"><OutlookMsgIcon /></div>
                                                            </div>
                                                            <div className='flex flex-col ml-4'>
                                                                <a style={{ color: 'blue', cursor: 'pointer', marginBottom: '2px' }} onClick={() => openFilePreview({ url: img.url, filename: img.filename })}>{img.filename}</a>
                                                                {user.role === "admin" && <button type="button" className='w-fit flex flex-row px-2 py-1 border rounded-md bg-red-900' onClick={() => deleteFiles(img)}><DeleteIcon /></button>}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                            const isPreviewable = /\.(pdf|png|jpg|jpeg|gif|webp|bmp|svg|img)$/i.test(img.filename || '');
                                            return (
                                                <div className='text-black h-auto w-52' key={img.id}>
                                                    <div className='flex flex-row p-2'>
                                                        <div onClick={() => openFilePreview({ url: img.url, filename: img.filename })} className="cursor-pointer">
                                                            {isPreviewable ? <embed src={img.url + '#page=1&view=FitH&scrollbar=0'} width="100px" height="auto" /> : <div className="w-[100px] h-[65px] flex items-center justify-center bg-gray-100 border rounded"><PaperClipIcon /></div>}
                                                        </div>
                                                        <div className='flex flex-col ml-4'>
                                                            <a style={{ color: 'blue', cursor: 'pointer', marginBottom: '2px' }} onClick={() => openFilePreview({ url: img.url, filename: img.filename })}>{img.filename}</a>
                                                            {user.role === "admin" && <button type="button" className='w-fit flex flex-row px-2 py-1 border rounded-md bg-red-900' onClick={() => deleteFiles(img)}><DeleteIcon /></button>}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>
                        <div className='basis-1/3 h-[calc(100vh_-_270px)] overflow-y-auto overflow-x-hidden'>
                            <div className='flex flex-col mb-5'><label className='font-bold mb-1'>{t('SupportTicketDetail.Status')}</label>
                                <div className='text-gray-800'>
                                    {user.role !== "admin" ? renderTicketStatus(ticketObj?.status) : (
                                        <select value={ticketObj?.status} onChange={(e) => changeStatus(e.target.value)} className='block w-60 px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'>
                                            <option value="Vytvorený">{t('SupportTicketList.Status.Vytvoreny')}</option>
                                            <option value="Čaká sa">{t('SupportTicketList.Status.CakaSa')}</option>
                                            <option value="Čaká sa na odpoveď">{t('SupportTicketList.Status.CakaSaNaOdpoved')}</option>
                                            <option value="V riešení">{t('SupportTicketList.Status.VRieseni')}</option>
                                            <option value="Pozastavený">{t('SupportTicketList.Status.Pozastaveny')}</option>
                                            <option value="Vyriešený">{t('SupportTicketList.Status.Vyrieseny')}</option>
                                        </select>
                                    )}
                                </div>
                            </div>
                            <div className='mb-2'>
                                <button className="flex flex-row gap-2 disabled:opacity-25" onClick={changeNotification} disabled={ticketObj?.status === "Vyriešený"}>
                                    {mentionUser?.pivot?.notification ? <BellSlashIcon /> : <i title={t('SupportTicketDetail.Notifications')} className="pi pi-bell" style={{ fontSize: '1.4rem', marginRight: 5 }}></i>}
                                    <span className='text-gray-800'>{mentionUser?.pivot?.notification ? t('SupportTicketDetail.DisableNotifications') : t('SupportTicketDetail.EnableNotifications')} {t('SupportTicketDetail.Notifications').toLowerCase()}</span>
                                </button>
                            </div>
                            <div className='mb-5'>
                                <button type="button" className='flex flex-row gap-2 disabled:opacity-25' disabled={user.id !== ticketObj?.contractor_id} onClick={() => changeStatus(t('SupportTicketList.Status.Vyrieseny'))}>
                                    <i title={t('SupportTicketDetail.ResolveSelf')} className="pi pi-arrow-right-arrow-left" style={{ fontSize: '1.4rem', marginRight: 5 }}></i>
                                    <span className='text-gray-800'>{t('SupportTicketDetail.ResolveSelf')}</span>
                                </button>
                            </div>
                            <div className='flex flex-col mb-5'><label className='font-bold mb-1'>{t('SupportTicketDetail.TicketType')}</label>{renderTicketType(ticketObj?.type)}</div>
                            <div className='flex flex-col mb-3'>
                                <label className='font-bold mb-1'>{t('SupportTicketDetail.WaitingForReplyFrom')}</label>
                                <Dropdown
                                    value={awaitingUserId}
                                    onChange={(e) => setAwaitingUser(e.value)}
                                    options={mentionOptions}
                                    optionLabel="name"
                                    optionValue="id"
                                    dataKey="id"
                                    showClear
                                    filter
                                    placeholder={t('SupportTicketDetail.SelectUsers')}
                                    className="w-60 px-4 mt-2 text-gray-800 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                                    {...primeOverlayPanelProps()}
                                />
                            </div>
                            <div className='flex flex-col mb-5'>
                                <label className='font-bold mb-1'>{t('SupportTicketDetail.Deadline')}</label>
                                <Calendar disabled={ticketObj?.status === "Vyriešený"} id="buttondisplay" name="dates" style={{ border: '1px solid #e5e7eb', borderRadius: '6px' }} value={ticketObj?.deadline ? new Date(ticketObj.deadline) : null} onChange={(e) => setDeadline(e.value)} showIcon className="w-60" dateFormat="dd.mm.yy" firstDayOfWeek={1} locale={locale || "sk"} />
                            </div>
                            <div className='flex flex-col mb-5'><label className='font-bold mb-1'>{t('SupportTicketDetail.InvolvedUsers')}</label>
                                {(ticketObj?.mentions ?? []).map((u) => (
                                    <div className='flex items-center gap-2 text-gray-800 mb-2' key={u.id}>
                                        <Avatar title={u?.employee?.name || u?.username} label={getUserInitials(u?.employee?.name || u?.username)} shape="circle" style={{ backgroundColor: getUserColor(u?.username), color: '#ffffff', marginRight: '5px' }} />
                                        <div className='flex flex-col'>
                                            <span>{u?.employee?.name || u?.username}</span>
                                            {ticketObj.contractor_id === u.id && <span className='text-sm text-gray-500'>{t('SupportTicketDetail.Founder')}</span>}
                                        </div>
                                    </div>
                                ))}
                                <div className='text-gray-800'>
                                    <button type="button" onClick={() => setShowAddUser(true)} className='flex items-center gap-2 disabled:opacity-25' disabled={ticketObj?.status === "Vyriešený"}>
                                        <i className='pi pi-plus mr-2 px-1' style={{ fontSize: "1.5rem" }}></i>{t('SupportTicketDetail.AddUser')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Dialog header={t('SupportTicketDetail.DetailDialogTitle')} visible={showDetail} style={{ width: '70vw' }} onHide={() => setShowDetail(false)}>
                <div className='w-auto flex flex-col'>
                    <div className='mb-2'><label className='font-semibold text-sm'>{t('SupportTicketDetail.Theme')}:</label><input name='theme' disabled value={ticketObj?.theme} className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40" /></div>
                    <div className='mb-2'><label className='font-semibold text-sm'>{t('SupportTicketDetail.Description')}:</label><MyEditor editorValue={ticketObj?.description} hideToolbar={true} hideFileUpload={true} formik={null} /></div>
                    <div className='mb-2'><label className='font-semibold text-sm'>{t('SupportTicketDetail.MaskNumber')}:</label><input name='mask_number' disabled value={ticketObj?.mask_number} className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40" /></div>
                    <div className='mb-2'><label className='font-semibold text-sm text-gray-800'>{t('SupportTicketDetail.Priority')}:</label><select disabled name='priority' value={ticketObj?.priority} className="maf-native-select block w-full text-sm px-4 py-2 text-gray-800 bg-white border border-gray-200 rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"><option value="low">{t('SupportTicketList.Priority.low')}</option><option value="normal">{t('SupportTicketList.Priority.normal')}</option><option value="high">{t('SupportTicketList.Priority.high')}</option><option value="blocker">{t('SupportTicketList.Priority.blocker')}</option></select></div>
                </div>
            </Dialog>
            <Dialog header={t('SupportTicketDetail.AddUserDialogTitle')} visible={showAddUser} style={{ width: '70vw' }} onHide={() => { setShowAddUser(false); setAddedUser(""); }}>
                <div className='w-auto flex flex-col'>
                    <Select
                        showSearch
                        optionFilterProp="children"
                        value={addedUser}
                        onChange={(value) => setAddedUser(value)}
                        className='block w-full mb-3 mt-3 text-xl text-gray-800 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'
                        size="large"
                        placeholder={t('SupportTicketDetail.SelectUser')}
                        {...antdPopupOverlayProps()}
                    >
                        {(state.users ?? []).map((u) => (<Select.Option key={u.id} value={u.id}>{u?.employee?.name || u?.username}</Select.Option>))}
                    </Select>
                </div>
                <div><button type="button" disabled={addedUser === ""} onClick={addUser} className='flex justify-center items-center mt-4 w-full h-10 font-medium mb-2 px-2 py-1 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none disabled:opacity-25'>{t('Common.Save')}</button></div>
            </Dialog>
        </>
    );
}
